import { type FC, useEffect } from "react";
import { useHistory } from "react-router-dom";

import { AlertBanner } from "@jti/core-components/components/AlertBanner";
import { CheckoutRoutes } from "@jti/fe-common";
import { useSelector } from "@jti/store/reduxHooks";
import { OrderExceptionCode } from "@jti/store/types";

import { ShippingDetailsSection } from "@checkout/components/ShippingDetailsSection";
import { DeliveryForm } from "@checkout/forms/DeliveryForm";
import { useFreeTrial } from "@checkout/hooks/useFreeTrial";

import styles from "./DeliveryPageContent.module.scss";

type DeliveryPageContentProps = {
  asideSubmitButtonWrapperRef: React.RefObject<HTMLDivElement | null>;
};

export const DeliveryPageContent: FC<DeliveryPageContentProps> = ({ asideSubmitButtonWrapperRef }) => {
  const history = useHistory();
  const { isFreeTrial } = useFreeTrial();

  const { shippingStepData } = useSelector((state) => state.standardCheckoutForm);
  const { error: checkoutError } = useSelector((state) => state.checkout);
  const checkoutErrorMessage =
    checkoutError.visible &&
    checkoutError.exception?.code === OrderExceptionCode.MagentoOrderCreationIssue &&
    checkoutError.exception?.message;

  const addresses = shippingStepData
    ? {
        shippingAddress: shippingStepData.shippingAddress,
        billingAddress: shippingStepData.billingAddress,
      }
    : undefined;

  const goToShippingStep = () => {
    const shippingPageUrl = new CheckoutRoutes({ isFreeTrial }).shipping;

    history.push(shippingPageUrl);
  };

  useEffect(() => {
    if (checkoutErrorMessage) {
      scrollTo(0, 0);
    }
  }, [checkoutErrorMessage]);

  return (
    <>
      {checkoutErrorMessage && (
        <div className={styles.errorBanner}>
          <AlertBanner tone="error" description={checkoutErrorMessage} />
        </div>
      )}

      <ShippingDetailsSection
        shippingAddress={addresses?.shippingAddress}
        companyName={shippingStepData?.shippingAddress.pickupInformation?.name}
        onEdit={goToShippingStep}
        className={styles.shippingDetailsSection}
        isEditButtonAvailable
      />

      <DeliveryForm asideSubmitButtonWrapperRef={asideSubmitButtonWrapperRef} />
    </>
  );
};
